/**
 * CustomerContext - Gestion de l'authentification client e-commerce
 * Séparé de AuthContext (qui gère les vendeurs/staff)
 *
 * Important: Le client est attaché à UN tenant (sa boutique locale)
 * - Lors de l'inscription, le client choisit son tenant
 * - Les livraisons se font uniquement depuis ce tenant
 * - Le client peut changer de tenant via ses paramètres
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Customer } from '../types/ecommerce.types';
import { ecommerceApi } from '../api/ecommerce.api';

interface CustomerContextType {
  customer: Customer | null;
  token: string | null; // JWT token for API calls
  isAuthenticated: boolean;
  isLoading: boolean;
  selectedTenantId: string | null; // Tenant sélectionné par le client
  login: (email: string, password: string, tenantId: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    tenantId: string,
    phone?: string,
    marketingConsent?: boolean
  ) => Promise<void>;
  logout: () => void;
  updateProfile: (data: Partial<Customer>) => Promise<void>;
  /**
   * Met a jour la liste des favoris DANS le profil deja charge.
   *
   * Le profil n'est lu qu'une fois, au montage : sans cela, un favori
   * enregistre cote serveur restait invisible partout ailleurs. Le coeur se
   * colorait sur la fiche (etat local du composant) puis la liste repartait
   * vide des qu'on changeait de page — signale le 21/09/2026, alors que les
   * favoris etaient bel et bien en base.
   *
   * On met a jour le CACHE plutot que de recharger tout le profil : un
   * aller-retour de plus a chaque coeur touche se sentirait sur un telephone
   * en 4G, pour une information qu'on vient d'ecrire et qu'on connait deja.
   */
  setFavoris: (ids: string[]) => void;
  setSelectedTenant: (tenantId: string) => void;
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

const CUSTOMER_TOKEN_KEY = 'customer_token';
const CUSTOMER_TENANT_KEY = 'customer_tenant_id';

export function CustomerProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Au montage, vérifier si un token existe
  useEffect(() => {
    const loadCustomer = async () => {
      const token = localStorage.getItem(CUSTOMER_TOKEN_KEY);
      const savedTenantId = localStorage.getItem(CUSTOMER_TENANT_KEY);

      if (token) {
        try {
          // Récupérer le profil client
          const profile = await ecommerceApi.auth.getProfile();
          setCustomer(profile);
          // Utiliser le tenant_id du profil (source de vérité)
          setSelectedTenantId(profile.tenant_id);
          // Synchroniser localStorage
          localStorage.setItem(CUSTOMER_TENANT_KEY, profile.tenant_id);
        } catch (error) {
          console.error('Erreur lors du chargement du profil client:', error);
          // Token invalide, tout supprimer
          localStorage.removeItem(CUSTOMER_TOKEN_KEY);
          localStorage.removeItem(CUSTOMER_TENANT_KEY);
        }
      } else if (savedTenantId) {
        // Pas de token mais tenant sauvegardé (pour affichage avant login)
        setSelectedTenantId(savedTenantId);
      }

      setIsLoading(false);
    };

    loadCustomer();
  }, []);

  const login = async (email: string, password: string, tenantId: string) => {
    const response = await ecommerceApi.auth.login({ email, password });
    localStorage.setItem(CUSTOMER_TOKEN_KEY, response.access_token);
    localStorage.setItem(CUSTOMER_TENANT_KEY, tenantId);
    setCustomer(response.customer);
    setSelectedTenantId(tenantId);
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    tenantId: string,
    phone?: string,
    marketingConsent?: boolean
  ) => {
    const response = await ecommerceApi.auth.register({
      email,
      password,
      first_name: firstName,
      last_name: lastName,
      phone,
      marketing_consent: marketingConsent ?? false,
    });
    localStorage.setItem(CUSTOMER_TOKEN_KEY, response.access_token);
    localStorage.setItem(CUSTOMER_TENANT_KEY, tenantId);
    setCustomer(response.customer);
    setSelectedTenantId(tenantId);
  };

  const logout = () => {
    localStorage.removeItem(CUSTOMER_TOKEN_KEY);
    // Garder le tenant_id pour la prochaine connexion
    setCustomer(null);
  };

  const updateProfile = async (data: Partial<Customer>) => {
    if (!customer) throw new Error('Client non authentifié');
    const updated = await ecommerceApi.auth.updateProfile(customer.id, data);
    setCustomer(updated);
  };

  const setSelectedTenant = (tenantId: string) => {
    localStorage.setItem(CUSTOMER_TENANT_KEY, tenantId);
    setSelectedTenantId(tenantId);
  };

  const setFavoris = (ids: string[]) => {
    setCustomer((c) =>
      c ? ({ ...c, preferences: { ...(c as any).preferences, favorite_products: ids } } as Customer) : c,
    );
  };

  return (
    <CustomerContext.Provider
      value={{
        customer,
        token: localStorage.getItem(CUSTOMER_TOKEN_KEY),
        isAuthenticated: !!customer,
        isLoading,
        selectedTenantId,
        login,
        register,
        logout,
        updateProfile,
        setFavoris,
        setSelectedTenant,
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer() {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error('useCustomer doit être utilisé dans un CustomerProvider');
  }
  return context;
}
